import { MicrophoneIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';

/**
 * A mic-button press shorter than this is a tap (toggle recording on and keep
 * it on); at or beyond it, the press is a hold (record only while held).
 */
const MIC_HOLD_MS = 300;

interface MicButtonProps {
  isRecording: boolean;
  isVoiceSupported: boolean;
  disabled?: boolean;
  /** Tap / keyboard activation: start a session, or finish the live one. */
  onToggleRecording: () => void;
  /** Pointer-down: begin a hold session immediately. */
  onPressStart: () => void;
  /** Pointer-up after a hold: finish and commit. */
  onPressEnd: () => void;
}

/**
 * The voice-input microphone. Tap toggles a recording session on/off;
 * press-and-hold records for the duration of the hold — the pointer twin of
 * the hold-right-Ctrl push-to-talk key (see hooks/useVoiceInput.ts).
 * Shared by InputForm (active conversation) and ChatWelcome (new chat).
 */
export function MicButton({
  isRecording,
  isVoiceSupported,
  disabled = false,
  onToggleRecording,
  onPressStart,
  onPressEnd,
}: MicButtonProps) {
  // Press bookkeeping. A pointer press both records and (via the browser)
  // fires a trailing click — the click must be suppressed so it can't
  // double-toggle; click is kept only as the keyboard-activation (Enter/Space)
  // path. Plain refs: none of this drives rendering.
  const pointerActiveRef = useRef(false);
  const downAtRef = useRef(0);
  const startedByPressRef = useRef(false);
  const suppressClickRef = useRef(false);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onPointerDown={e => {
          e.stopPropagation();
          // Unsupported browser: let the trailing click fire the notice.
          if (!isVoiceSupported) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          pointerActiveRef.current = true;
          downAtRef.current = Date.now();
          startedByPressRef.current = !isRecording;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // pointer capture is best-effort (absent in happy-dom)
          }
          if (!isRecording) onPressStart();
        }}
        onPointerUp={() => {
          if (!pointerActiveRef.current) return;
          pointerActiveRef.current = false;
          suppressClickRef.current = true;
          const wasHold = Date.now() - downAtRef.current >= MIC_HOLD_MS;
          if (startedByPressRef.current) {
            // Started by this press: a hold ends on release; a quick tap
            // converts into a toggle session and keeps recording.
            if (wasHold) onPressEnd();
          } else {
            // Press began while already recording — any release stops it.
            onPressEnd();
          }
        }}
        onPointerCancel={() => {
          if (!pointerActiveRef.current) return;
          pointerActiveRef.current = false;
          suppressClickRef.current = true;
          // Commit what was heard rather than wedge a captured press.
          onPressEnd();
        }}
        onContextMenu={e => {
          // Touch long-press opens the context menu mid-capture.
          if (pointerActiveRef.current) e.preventDefault();
        }}
        onClick={e => {
          e.stopPropagation();
          const wasSuppressed = suppressClickRef.current;
          suppressClickRef.current = false;
          // Keyboard activations (Enter/Space) report detail 0; only a trailing
          // pointer click (detail > 0) is ever suppressed, so a stranded flag
          // (pointercancel fires no click) can't swallow keyboard activation.
          if (wasSuppressed && e.detail > 0) return;
          onToggleRecording();
        }}
        aria-pressed={isRecording}
        aria-label={isRecording ? 'Stop voice input' : 'Start voice input'}
        className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          isRecording
            ? 'bg-error hover:bg-error/90 animate-pulse text-white'
            : 'text-chariot-text-secondary hover:text-chariot-text-primary'
        }`}
        title={
          isRecording
            ? 'Recording — release or click to insert, Esc to cancel'
            : 'Voice input — click to toggle, or hold right Ctrl (fn) to talk'
        }
      >
        <MicrophoneIcon className="size-4" />
      </button>
      {/* Screen-reader announcement of recording state transitions. */}
      <span className="sr-only" role="status" aria-live="polite">
        {isRecording
          ? 'Recording. Release the key or click the microphone to insert your dictation.'
          : ''}
      </span>
    </>
  );
}
